/**
 * The shared vocabulary for a row's expanded detail panel.
 *
 * Extracted from AgreementDetail on 2026-08-09 when Hotels and Customers gained
 * their own panels. These three pieces carry a UX decision that has to be the
 * same everywhere — see `Field` on why a missing value renders an em-dash
 * instead of disappearing. Re-declaring them per page would let one panel start
 * hiding empty fields while its neighbours kept showing them, and each would
 * look self-consistent.
 *
 * Styling is in crm-styles.css under `.crm-scope` (`.detail-panel`,
 * `.detail-group`, `.detail-grid`, `.detail-field`, `.detail-table`).
 */
import type React from 'react';

/**
 * One labelled value.
 *
 * Renders an em-dash for anything missing rather than hiding the row: "we have
 * no Sabre code for this hotel" is information the team acts on, and a field
 * that silently disappears reads as "not applicable" instead of "not known".
 *
 * `children` wins over `value` so a caller can supply a link or badge; passing
 * neither is what produces the dash. Note `0` and `false` must survive — an
 * emptiness test of `!value` would blank a legitimate zero (nights, bookings,
 * spend), so the check is explicitly against null/undefined/''.
 */
export function Field({
  label,
  value,
  mono,
  children,
}: {
  label: string;
  value?: string | number | null;
  mono?: boolean;
  children?: React.ReactNode;
}) {
  const filled =
    children != null || (value !== null && value !== undefined && value !== '');
  return (
    <div className="detail-field">
      <dt>{label}</dt>
      <dd className={mono ? 'mono' : undefined}>
        {filled ? (children ?? value) : <span className="muted">—</span>}
      </dd>
    </div>
  );
}

/**
 * A titled section of a panel. Callers omit a Group entirely when its child
 * COLLECTION is empty (an empty rates table is noise); they do not omit
 * individual empty fields (see `Field`).
 */
export function Group({
  label,
  children,
  action,
  testId,
}: {
  label: string;
  children: React.ReactNode;
  /**
   * Rendered inline with the group's heading, right of the label.
   *
   * Added 2026-09-10 for the agreement drawer's edit button — Jeff asked for
   * "something clean and inline with that header". A slot rather than a
   * hard-coded button because the next group that wants one should not need a
   * second layout: the heading and its affordance are one row, and only this
   * component should know how that row is built.
   */
  action?: React.ReactNode;
  testId?: string;
}) {
  return (
    <section className="detail-group" data-testid={testId}>
      {action ? (
        /*
         * The action sits IMMEDIATELY AFTER the label, not pushed to the far
         * edge. `space-between` was the first instinct and it was wrong: the
         * detail panel spans the whole table, so at 1500px it put the button
         * about 1,200px from the heading it belongs to and the two read as
         * unrelated. Jeff asked for "something clean and inline with that
         * header", and inline means adjacent.
         *
         * `baseline` rather than `center`: the label is 10.5px uppercase with
         * wide tracking, so centring it against a normal control makes the
         * label look as though it has slipped downward.
         */
        <div style={{ alignItems: 'baseline', display: 'flex', gap: 10 }}>
          <h4 className="detail-group-label">{label}</h4>
          {action}
        </div>
      ) : (
        <h4 className="detail-group-label">{label}</h4>
      )}
      {children}
    </section>
  );
}
